/**
 * Form Validation Utilities
 * 
 * Client-side validation for forms
 * - Field validators
 * - Form validation helpers
 * - Error message formatting
 * 
 * Requirements: 12.4
 */

/**
 * Validation result
 */
export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Form errors object
 */
export type FormErrors<T> = Partial<Record<keyof T, string>>;

/**
 * Validator function type
 */
export type Validator<T = any> = (value: T) => ValidationResult;

/**
 * Required field validator
 */
export function required(message: string = 'This field is required'): Validator {
  return (value: any): ValidationResult => {
    if (value === null || value === undefined || value === '') {
      return { isValid: false, error: message };
    }
    if (typeof value === 'string' && value.trim() === '') {
      return { isValid: false, error: message };
    }
    return { isValid: true };
  };
}

/**
 * Email validator
 */
export function email(message: string = 'Please enter a valid email address'): Validator<string> {
  return (value: string): ValidationResult => {
    if (!value) {
      return { isValid: true }; // Use required() for required fields
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return { isValid: false, error: message };
    }
    
    return { isValid: true };
  };
}

/**
 * Phone number validator (US format)
 */
export function phone(message: string = 'Please enter a valid phone number'): Validator<string> {
  return (value: string): ValidationResult => {
    if (!value) {
      return { isValid: true }; // Use required() for required fields
    }
    
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    
    // Check if it's 10 or 11 digits (with or without country code)
    if (digits.length !== 10 && digits.length !== 11) {
      return { isValid: false, error: message };
    }
    
    return { isValid: true };
  };
}

/**
 * URL validator
 */
export function url(message: string = 'Please enter a valid URL (must start with http:// or https://)'): Validator<string> {
  return (value: string): ValidationResult => {
    if (!value) {
      return { isValid: true }; // Use required() for required fields
    }
    
    // Check if URL starts with http:// or https://
    if (!value.startsWith('http://') && !value.startsWith('https://')) {
      return { isValid: false, error: message };
    }
    
    // Try to parse as URL
    try {
      new URL(value);
      return { isValid: true };
    } catch {
      return { isValid: false, error: message };
    }
  };
}

/**
 * Minimum length validator
 */
export function minLength(min: number, message?: string): Validator<string> {
  return (value: string): ValidationResult => {
    if (!value) {
      return { isValid: true }; // Use required() for required fields
    }
    
    if (value.length < min) {
      return {
        isValid: false,
        error: message || `Must be at least ${min} characters`,
      };
    }
    
    return { isValid: true };
  };
}

/**
 * Maximum length validator
 */
export function maxLength(max: number, message?: string): Validator<string> {
  return (value: string): ValidationResult => {
    if (!value) {
      return { isValid: true };
    }
    
    if (value.length > max) {
      return {
        isValid: false,
        error: message || `Must be no more than ${max} characters`,
      };
    }
    
    return { isValid: true };
  };
}

/**
 * Pattern validator (regex)
 */
export function pattern(regex: RegExp, message: string): Validator<string> {
  return (value: string): ValidationResult => {
    if (!value) {
      return { isValid: true }; // Use required() for required fields
    }
    
    if (!regex.test(value)) {
      return { isValid: false, error: message };
    }
    
    return { isValid: true };
  };
}

/**
 * Date validator (must be valid date)
 */
export function validDate(message: string = 'Please enter a valid date'): Validator<string> {
  return (value: string): ValidationResult => {
    if (!value) {
      return { isValid: true }; // Use required() for required fields
    }
    
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return { isValid: false, error: message };
    }
    
    return { isValid: true };
  };
}

/**
 * Future date validator (must be in the future)
 */
export function futureDate(message: string = 'Date must be in the future'): Validator<string> {
  return (value: string): ValidationResult => {
    if (!value) {
      return { isValid: true }; // Use required() for required fields
    }
    
    const date = new Date(value);
    const now = new Date();
    
    if (date <= now) {
      return { isValid: false, error: message };
    }
    
    return { isValid: true };
  };
}

/**
 * Date range validator (end date must be after start date)
 */
export function dateRange(
  startDate: string,
  message: string = 'End date must be after start date'
): Validator<string> {
  return (endDate: string): ValidationResult => {
    if (!endDate || !startDate) {
      return { isValid: true };
    }
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (end <= start) {
      return { isValid: false, error: message };
    }
    
    return { isValid: true };
  };
}

/**
 * Compose multiple validators
 */
export function compose(...validators: Validator[]): Validator {
  return (value: any): ValidationResult => {
    for (const validator of validators) {
      const result = validator(value);
      if (!result.isValid) {
        return result;
      }
    }
    return { isValid: true };
  };
}

/**
 * Validate a single field with validators
 */
export function validateField(value: any, validators: Validator[]): string | undefined {
  for (const validator of validators) {
    const result = validator(value);
    if (!result.isValid) {
      return result.error;
    }
  }
  return undefined;
}

/**
 * Validate entire form
 */
export function validateForm<T extends Record<string, any>>(
  values: T,
  rules: Partial<Record<keyof T, Validator[]>>
): FormErrors<T> {
  const errors: FormErrors<T> = {};
  
  for (const field in rules) {
    const validators = rules[field];
    if (validators) {
      const error = validateField(values[field], validators);
      if (error) {
        errors[field] = error;
      }
    }
  }
  
  return errors;
}

/**
 * Check if form has any errors
 */
export function hasErrors<T>(errors: FormErrors<T>): boolean {
  return Object.keys(errors).length > 0;
}

/**
 * Get first error message from form errors
 */
export function getFirstError<T>(errors: FormErrors<T>): string | undefined {
  const keys = Object.keys(errors) as Array<keyof T>;
  if (keys.length === 0) {
    return undefined;
  }
  return errors[keys[0]];
}
